import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  BuilderPage,
  BuilderPageSearch,
  ColumnAdapter,
  COND_FEATURE_FLAG_ALL,
  ConditionFeatureFlag,
  ConditionValue,
  IActionRepeaterValue,
  IActionType,
} from '@makyoapp/makyo-common'
import { Column16 } from '@makyoapp/makyo-icon/icon'
import { Divider, Field, SelectDropdown } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { debounce } from '@/shared/utils'
import { AccordionItem } from '@/components/Accordion/Accordion'
import { ActionRepeaterV2 } from '@/components/ActionV2/ActionRepeaterV2'
import { VisibilitySelector } from '@/components/BehaviourV2/VisibilitySelector'
import { TableDataSourceDropdown } from '@/components/Builder/TableDataSourceDropdown'
import { RenderColumn } from '@/components/RenderColumn'

/**
 * @todo
 */
function ActionAccordion({ page, pageIdx }: { page: BuilderPage; pageIdx: number }) {
  const { updatePageAlt } = useAppPagesContext()

  const [values, setValues] = useState<IActionRepeaterValue[]>(page.actions.onLoad)

  const handleChange = useCallback(
    async (nextValues: IActionRepeaterValue[]) => {
      setValues(nextValues)
      await updatePageAlt(pageIdx, (prev) => {
        prev.actions.onLoad = nextValues
      })
    },
    [updatePageAlt, pageIdx]
  )

  return (
    <AccordionItem itemId='action-settings' title={<Trans>Actions</Trans>}>
      <Field isVertical>
        <label>
          <Trans>On Screen Load</Trans>
        </label>
        <ActionRepeaterV2
          id={`${page.id}-on-load-action`}
          values={values}
          onChange={handleChange}
          excludeActions={[
            IActionType.None,
            IActionType.ShowDialog,
            IActionType.ComposeEmail,
            IActionType.MakePhoneCall,
            IActionType.StartSMS,
            IActionType.StartWhatsApp,
            IActionType.Clipboard,
            IActionType.ShowDetailsScreen,
            IActionType.OpenMenu,
            IActionType.CloseAlert,
            IActionType.PushNotification,
            IActionType.PerformNextAction,
            IActionType.FormAddRow,
            IActionType.FormUpdateRow,
            IActionType.UpdateRow,
            IActionType.IncrementValue,
            IActionType.ToggleValue,
            IActionType.DuplicateRow,
            IActionType.DeleteRow,
          ]}
        />
      </Field>
    </AccordionItem>
  )
}

function BehaviorAccordion({ page, pageIdx }: { page: BuilderPage; pageIdx: number }) {
  const { updatePageAlt } = useAppPagesContext()
  const [localValue, setLocalValue] = useState<ConditionValue | null>(page.visibility ?? null)

  useEffect(() => {
    setLocalValue(page.visibility ?? null)
  }, [page.visibility])

  const handleVisibilityChange = useCallback(
    (nextValue: ConditionValue | null) => {
      setLocalValue(nextValue)

      updatePageAlt(pageIdx, (prev) => {
        if (nextValue === null) {
          prev.visibility = undefined as any
        } else {
          prev.visibility = nextValue
        }
      })
    },
    [pageIdx, updatePageAlt]
  )

  return (
    <AccordionItem itemId='behavior-settings' title={<Trans>Behavior</Trans>}>
      <VisibilitySelector
        value={localValue}
        featureFlags={COND_FEATURE_FLAG_ALL & ~ConditionFeatureFlag.CurrentRow}
        onChange={handleVisibilityChange}
      />
    </AccordionItem>
  )
}

/**
 * @todo
 */

function SearchAccordion({ page, pageIdx }: { page: BuilderPage; pageIdx: number }) {
  const { updatePageAlt } = useAppPagesContext()
  const { tables } = useDbClientContext()
  const [searchSettings, setSearchSettings] = useState(page.searchSettings)

  const debouncedUpdatePageAlt = useMemo(
    () =>
      debounce((updater: (prev: BuilderPage) => void) => {
        updatePageAlt(pageIdx, updater)
      }, 800),
    [updatePageAlt, pageIdx]
  )

  const table = useMemo(() => {
    return tables.find((tb) => tb.id === searchSettings?.table)
  }, [searchSettings, tables])

  const colOptions = useMemo(() => {
    return [
      { id: '', name: 'Select Column' } as ColumnAdapter,
      ...(table?.columns.filter((c) => {
        return ![ColumnTypes.Connection].includes(c.type)
      }) || []),
    ]
  }, [table])

  const handleTableChange = useCallback(
    (nextValue: string | undefined) => {
      // Immediate local state update for UI responsiveness
      setSearchSettings((prev) => ({
        ...prev,
        table: nextValue,
        columns: [],
        mediaColumn: undefined,
        contentColumn: undefined,
        headerColumn: undefined,
        metadata1Column: undefined,
        metadata2Column: undefined,
      }))

      // Debounced backend state update
      debouncedUpdatePageAlt((prev) => {
        if (prev.searchSettings?.table !== nextValue) {
          prev.searchSettings = {
            table: nextValue,
            columns: [],
            mediaColumn: undefined,
            contentColumn: undefined,
            headerColumn: undefined,
            metadata1Column: undefined,
            metadata2Column: undefined,
          }
        }
      })
    },
    [debouncedUpdatePageAlt]
  )

  const handleColumnsChange = useCallback(
    (columns: ColumnAdapter[]) => {
      // Immediate local state update for UI responsiveness
      setSearchSettings((prev) => ({
        ...prev,
        columns: columns.map((c) => c.id),
      }))

      // Debounced backend state update
      debouncedUpdatePageAlt((prev) => {
        if (prev.searchSettings) {
          prev.searchSettings.columns = columns.map((c) => c.id)
        }
      })
    },
    [debouncedUpdatePageAlt]
  )
  return (
    <AccordionItem itemId='search-settings' title={<Trans>Search Data</Trans>}>
      <Field isVertical>
        <label>
          <Trans>Table</Trans>
        </label>
        <TableDataSourceDropdown
          id='page-search-table-source'
          tables={tables}
          value={searchSettings?.table || ''}
          onChange={handleTableChange}
        />
      </Field>
      <Field isVertical>
        <label>
          <Trans>Columns</Trans>
        </label>
        <SelectDropdown
          id='page-search-columns-source'
          withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
          multiple
          onChange={handleColumnsChange}
          value={colOptions.filter((col) => searchSettings?.columns?.includes(col.id))}
          optionLabel={(col) => `${(col as unknown as ColumnAdapter)?.id} ${(col as unknown as ColumnAdapter)?.name}`}
          options={colOptions as never}
          placeholder={
            <div
              className='flex-c'
              style={{
                paddingInlineStart: '.5rem',
              }}
            >
              <Column16 />
              <div>
                <Trans>Select Columns</Trans>
              </div>
            </div>
          }
          renderOption={(opt) => {
            return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
          }}
        />
      </Field>
      <Divider />
      <div className='uppercase py-2 text-light text-m-sm'>
        <Trans>Search Result</Trans>
      </div>
      {(
        [
          ['headerColumn', 'Header', undefined],
          ['contentColumn', 'Content', undefined],
          ['mediaColumn', 'Media', [ColumnTypes.URL, ColumnTypes.Attachment, ColumnTypes.Image]],
          [
            'metadata1Column',
            'Metadata 1',
            [
              ColumnTypes.Text,
              ColumnTypes.Rating,
              ColumnTypes.DateTime,
              ColumnTypes.Number,
              ColumnTypes.Email,
              ColumnTypes.Phone,
            ],
          ],
          [
            'metadata2Column',
            'Metadata 2',
            [
              ColumnTypes.Text,
              ColumnTypes.Rating,
              ColumnTypes.DateTime,
              ColumnTypes.Number,
              ColumnTypes.Email,
              ColumnTypes.Phone,
            ],
          ],
        ] as [
          keyof Pick<
            BuilderPageSearch,
            'metadata1Column' | 'metadata2Column' | 'mediaColumn' | 'headerColumn' | 'contentColumn'
          >,
          string,
          ColumnTypes[] | undefined,
        ][]
      ).map((pc) => {
        const key = pc[0]
        const label = pc[1]
        const colTypes = pc[2]
        return (
          <Field key={key} isVertical>
            <label>
              <Trans>{label}</Trans>
            </label>
            <SelectDropdown
              id={`page-search-${key}-source`}
              withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
              onChange={async (col) => {
                setSearchSettings((prev) => ({
                  ...prev,
                  [key]: col.id,
                })) // Immediate UI update

                debouncedUpdatePageAlt((prev) => {
                  if (prev.searchSettings) {
                    prev.searchSettings[key] = col.id
                  }
                })
              }}
              value={colOptions.find((col) => col.id === searchSettings?.[key])}
              optionLabel={(col) => `${col?.id} ${col?.name}`}
              options={
                colTypes
                  ? colOptions.filter((c) => {
                      if (c.type === ColumnTypes.Lookup) {
                        const connCol = table?.columns.find((cc) => {
                          return cc.id === c?.lookup?.connColId
                        })
                        if (connCol) {
                          const connStg = connCol.connections
                          const foreignTable = tables.find((tb) => tb.id === connStg?.fTableId)
                          const foreignColumn = foreignTable?.columns.find((fc) => fc.id === c?.lookup?.colId)
                          return foreignColumn && colTypes.includes(foreignColumn.type)
                        } else {
                          return false
                        }
                      }
                      return colTypes.includes(c.type) || c.id === ''
                    })
                  : colOptions
              }
              placeholder={
                <div
                  className='flex-c'
                  style={{
                    paddingInlineStart: '.5rem',
                  }}
                >
                  <Column16 />
                  <div>
                    <Trans>Select Columns</Trans>
                  </div>
                </div>
              }
              renderOption={(opt) => {
                return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
              }}
            />
          </Field>
        )
      })}
    </AccordionItem>
  )
}

export { ActionAccordion, BehaviorAccordion, SearchAccordion }
